import { z } from 'zod';
import {
  amountMinorSchema,
  currencySchema,
  MEDIA_FILE_PATTERN,
  tenantMediaSchema,
} from './catalog.js';
import { emailSchema, membershipSchema, tenantRoleSchema } from './identity.js';
import { issuedInvitationSchema } from './invitations.js';
import { pageQuerySchema } from './pagination.js';
import {
  CONTENT_PAGE_KINDS,
  contentPageKindSchema,
  contentPageSlugSchema,
  customerStatusSchema,
  fulfillmentMethodSchema,
  orderAddressSchema,
  orderContactSchema,
  orderItemSchema,
  orderStatusSchema,
  popupPlacementSchema,
  storeLinkSchema,
  type ContentPageKind,
  type OrderStatus,
} from './storefront.js';

/**
 * The tenant admin surface (docs/TENANT_ADMIN.md, spec §04): what a store owner and the store's
 * staff manage. The tenant always comes from the session, never from these shapes. Updates of
 * small entities are whole replacements guarded by `version` (optimistic lock); a stale version
 * answers 409 CONFLICT.
 */

const versionSchema = z.number().int().min(1);
/** Free text that may be absent. Clients send null for an empty field. */
const optionalText = (max: number) => z.string().trim().min(1).max(max).nullable();
const requiredText = (max: number) => z.string().trim().min(1).max(max);
const instantSchema = z.iso.datetime({ offset: true });

/** `startsAt` must come before `endsAt` when both are set. */
function windowIsOrdered(value: { startsAt: string | null; endsAt: string | null }): boolean {
  return (
    value.startsAt === null ||
    value.endsAt === null ||
    Date.parse(value.startsAt) < Date.parse(value.endsAt)
  );
}
const WINDOW_MESSAGE = { message: 'the end must come after the start', path: ['endsAt'] };

// ---------------------------------------------------------------------------------------------
// Media (uploads of the store: logo, favicon, banners, popups, product images)
// ---------------------------------------------------------------------------------------------

/** Upload limit for one image (the request body is the image itself). */
export const MAX_UPLOAD_BYTES = 5 * 1024 * 1024;

/**
 * The largest image a store may upload, by its header (a small file can decode to a huge
 * bitmap): at most this many pixels on a side, and this many pixels in all.
 */
export const MAX_IMAGE_SIDE = 6000;
export const MAX_IMAGE_PIXELS = 24_000_000;

/** What one store may keep in uploads (re-uploading a file it already has costs nothing). */
export const MAX_TENANT_UPLOADS = 2000;
export const MAX_TENANT_UPLOAD_BYTES = 2 * 1024 * 1024 * 1024;

/** The image types a store may upload; the API decides the type from the bytes, not the header. */
export const UPLOAD_IMAGE_TYPES = [
  'image/jpeg',
  'image/png',
  'image/gif',
  'image/webp',
  'image/avif',
] as const;

export const mediaFileParamsSchema = z.object({ file: z.string().regex(MEDIA_FILE_PATTERN) });

// ---------------------------------------------------------------------------------------------
// Store settings (spec §04 "הגדרות חנות", "אנליטיקס משלו")
// ---------------------------------------------------------------------------------------------

export const DEFAULT_TIME_ZONE = 'Asia/Jerusalem';

/**
 * An IANA zone name (`Region/City`, `UTC`): never an offset form such as `+02:00`, which
 * PostgreSQL reads with the opposite sign. Same pattern as the database check.
 */
export const TIME_ZONE_NAME_PATTERN = /^[A-Za-z][A-Za-z_]*(?:\/[A-Za-z0-9_+-]+)*$/;

/**
 * An IANA time zone name the runtime knows (checked with Intl, in the browser and on the
 * server). The API also requires PostgreSQL to know it and stores PostgreSQL's spelling.
 */
export const timeZoneSchema = z
  .string()
  .trim()
  .min(1)
  .max(64)
  .regex(TIME_ZONE_NAME_PATTERN, 'a time zone name such as Asia/Jerusalem')
  .refine((zone) => {
    try {
      new Intl.DateTimeFormat('en', { timeZone: zone });
      return true;
    } catch {
      return false;
    }
  }, 'unknown time zone');

/** Google Analytics 4 measurement ID, e.g. `G-AB12CD34EF`. */
export const ga4MeasurementIdSchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^G-[A-Z0-9]{4,15}$/, 'a GA4 measurement ID looks like G-XXXXXXXXXX');

/** Meta (Facebook) pixel ID: digits only. */
export const metaPixelIdSchema = z
  .string()
  .trim()
  .regex(/^\d{6,20}$/, 'a pixel ID is 6 to 20 digits');

/** Israeli company / licensed-dealer number (ח.פ. / ע.מ.). */
export const businessIdSchema = z
  .string()
  .trim()
  .regex(/^\d{8,9}$/, '8 or 9 digits');

const settingsPhoneSchema = z
  .string()
  .trim()
  .regex(/^\+?[0-9][0-9 -]{7,18}[0-9]$/, 'invalid phone number');

const storeSettingsFields = {
  /** The storefront is open to shoppers. Closed: a neutral "closed for now" page, never indexed. */
  siteOpen: z.boolean(),
  /** Shoppers can send quote requests. Off: the catalog stays browsable, requests are closed. */
  ordersEnabled: z.boolean(),
  deliveryEnabled: z.boolean(),
  pickupEnabled: z.boolean(),
  pickupAddress: optionalText(300),
  /** Shoppers may send a quote request without an account. */
  guestCheckoutEnabled: z.boolean(),
  /** Shoppers may open an account. */
  registrationEnabled: z.boolean(),
  /**
   * Shoppers see the store's list prices. Off: no price reaches a shopper at all (adr/0022).
   * Either way the store takes quote requests, not orders (adr/0024).
   */
  showPrices: z.boolean(),
  /** A tracked product at or below this quantity counts as low on stock (unless it has its own). */
  lowStockThreshold: z.number().int().min(0).max(1_000_000),
  /** From when stores took orders; nothing reads it now (adr/0022). */
  freeShippingThresholdMinor: amountMinorSchema.nullable(),
  currency: currencySchema,
  timeZone: timeZoneSchema,
  business: z.object({
    /** The legal name on documents (the store name is shown when empty). */
    legalName: optionalText(120),
    businessId: businessIdSchema.nullable(),
    phone: settingsPhoneSchema.nullable(),
    /** The number shoppers write to on WhatsApp; the phone is used when it is empty. */
    whatsapp: settingsPhoneSchema.nullable(),
    email: emailSchema.nullable(),
    address: optionalText(300),
    website: z
      .url({ protocol: /^https?$/ })
      .max(300)
      .nullable(),
  }),
  seo: z.object({
    title: optionalText(70),
    description: optionalText(320),
  }),
  analytics: z.object({
    ga4MeasurementId: ga4MeasurementIdSchema.nullable(),
    metaPixelId: metaPixelIdSchema.nullable(),
  }),
};

function canFulfil(settings: { deliveryEnabled: boolean; pickupEnabled: boolean }): boolean {
  return settings.deliveryEnabled || settings.pickupEnabled;
}
const FULFIL_MESSAGE = {
  message: 'keep delivery or pickup on',
  path: ['deliveryEnabled'],
};

export const storeSettingsSchema = z.object({
  ...storeSettingsFields,
  version: z.number().int(),
  updatedAt: z.iso.datetime(),
});
export type StoreSettings = z.infer<typeof storeSettingsSchema>;

export const updateStoreSettingsRequestSchema = z
  .object({ ...storeSettingsFields, version: versionSchema })
  .refine(canFulfil, FULFIL_MESSAGE);
export type UpdateStoreSettingsRequest = z.infer<typeof updateStoreSettingsRequestSchema>;

// ---------------------------------------------------------------------------------------------
// Notices of new quote requests by email (adr/0027)
// ---------------------------------------------------------------------------------------------

/**
 * A Google "app password": 16 letters, shown by Google in four groups. Spaces are dropped, so it
 * can be pasted as Google shows it.
 */
export const gmailAppPasswordSchema = z
  .string()
  .max(40)
  .transform((value) => value.replace(/\s+/g, '').toLowerCase())
  .pipe(z.string().regex(/^[a-z]{16}$/, 'an app password is 16 letters'));

/**
 * The Gmail account the store connected: every new quote request is mailed from it to itself.
 * The password is never read back.
 */
export const storeMailSchema = z.object({
  gmailAddress: z.string().nullable(),
  /** When the last notice could not be sent (Google refused the password); null while all is well. */
  failedAt: z.iso.datetime().nullable(),
});
export type StoreMail = z.infer<typeof storeMailSchema>;

export const connectStoreMailRequestSchema = z.object({
  gmailAddress: emailSchema,
  appPassword: gmailAppPasswordSchema,
});
export type ConnectStoreMailRequest = z.infer<typeof connectStoreMailRequestSchema>;

/** Why Google did not take the account: the details are wrong, or Gmail could not be reached. */
export const STORE_MAIL_PROBLEMS = ['REFUSED', 'UNREACHABLE'] as const;
export type StoreMailProblem = (typeof STORE_MAIL_PROBLEMS)[number];

/**
 * How shoppers and their requests reach one store, as the platform staff manage it for the store
 * (adr/0028): the WhatsApp number and the connected Gmail account.
 */
export const storeContactSchema = z.object({
  /** The store's phone: shoppers write to it on WhatsApp while there is no WhatsApp number. */
  phone: z.string().nullable(),
  whatsapp: z.string().nullable(),
  mail: storeMailSchema,
});
export type StoreContact = z.infer<typeof storeContactSchema>;

export const updateStoreWhatsappRequestSchema = z.object({
  /** Null removes the number: the store's phone is used again. */
  whatsapp: settingsPhoneSchema.nullable(),
});
export type UpdateStoreWhatsappRequest = z.infer<typeof updateStoreWhatsappRequestSchema>;

// ---------------------------------------------------------------------------------------------
// Branding: the store's logo and favicon. The store's colours are not the store's to change.
// ---------------------------------------------------------------------------------------------

export const storeBrandingSchema = z.object({
  logo: tenantMediaSchema.nullable(),
  favicon: tenantMediaSchema.nullable(),
  version: z.number().int(),
});
export type StoreBranding = z.infer<typeof storeBrandingSchema>;

export const updateStoreBrandingRequestSchema = z.object({
  logoMediaId: z.uuid().nullable(),
  faviconMediaId: z.uuid().nullable(),
  version: versionSchema,
});
export type UpdateStoreBrandingRequest = z.infer<typeof updateStoreBrandingRequestSchema>;

// ---------------------------------------------------------------------------------------------
// Home page (spec §04 "באנרים בדף הבית")
// ---------------------------------------------------------------------------------------------

export const MAX_HOME_SLIDES = 10;
export const MAX_FEATURED_CATEGORIES = 12;

const heroSchema = z.object({
  /**
   * The short line above the title and the title's second line (adr/0034). Optional in a
   * request: a form that does not send them leaves them as they are.
   */
  eyebrow: optionalText(80).optional(),
  title: optionalText(120),
  highlight: optionalText(80).optional(),
  text: optionalText(500),
  buttonLabel: optionalText(40),
  buttonLink: storeLinkSchema.nullable(),
});

export const homeSlideSchema = z.object({
  id: z.uuid(),
  image: tenantMediaSchema,
  title: z.string().nullable(),
  text: z.string().nullable(),
  buttonLabel: z.string().nullable(),
  link: z.string().nullable(),
  position: z.number().int(),
  isActive: z.boolean(),
  version: z.number().int(),
});
export type HomeSlide = z.infer<typeof homeSlideSchema>;

export const storeHomeSchema = z.object({
  hero: z.object({
    eyebrow: z.string().nullable(),
    title: z.string().nullable(),
    highlight: z.string().nullable(),
    text: z.string().nullable(),
    buttonLabel: z.string().nullable(),
    buttonLink: z.string().nullable(),
  }),
  showNewest: z.boolean(),
  /** In display order. */
  featuredCategoryIds: z.array(z.uuid()),
  /** Every slide, active or not, in display order. */
  slides: z.array(homeSlideSchema),
  version: z.number().int(),
});
export type StoreHome = z.infer<typeof storeHomeSchema>;

export const updateStoreHomeRequestSchema = z.object({
  hero: heroSchema,
  showNewest: z.boolean(),
  featuredCategoryIds: z
    .array(z.uuid())
    .max(MAX_FEATURED_CATEGORIES)
    .refine((ids) => new Set(ids).size === ids.length, 'duplicate category'),
  version: versionSchema,
});
export type UpdateStoreHomeRequest = z.infer<typeof updateStoreHomeRequestSchema>;

const slideFields = {
  mediaId: z.uuid(),
  title: optionalText(120),
  text: optionalText(300),
  buttonLabel: optionalText(40),
  link: storeLinkSchema.nullable(),
  position: z.number().int().min(0).max(1000),
  isActive: z.boolean(),
};

export const createHomeSlideRequestSchema = z.object(slideFields);
export type CreateHomeSlideRequest = z.infer<typeof createHomeSlideRequestSchema>;

export const updateHomeSlideRequestSchema = z.object({ ...slideFields, version: versionSchema });
export type UpdateHomeSlideRequest = z.infer<typeof updateHomeSlideRequestSchema>;

// ---------------------------------------------------------------------------------------------
// Content pages (spec §04 "עמודי תוכן")
// ---------------------------------------------------------------------------------------------

/** The address each standard page gets unless the owner chooses another. */
export const CONTENT_PAGE_DEFAULT_SLUGS: Readonly<Record<ContentPageKind, string | null>> = {
  ABOUT: 'about',
  CONTACT: 'contact',
  FAQ: 'faq',
  TERMS: 'terms',
  PRIVACY: 'privacy',
  ACCESSIBILITY: 'accessibility',
  CUSTOM: null,
};
export const STANDARD_CONTENT_PAGE_KINDS = CONTENT_PAGE_KINDS.filter((kind) => kind !== 'CUSTOM');

export const MAX_CONTENT_PAGES = 30;

export const contentPageSummarySchema = z.object({
  id: z.uuid(),
  kind: contentPageKindSchema,
  slug: z.string(),
  title: z.string(),
  isPublished: z.boolean(),
  position: z.number().int(),
  version: z.number().int(),
  updatedAt: z.iso.datetime(),
});
export type ContentPageSummary = z.infer<typeof contentPageSummarySchema>;

export const contentPageSchema = contentPageSummarySchema.extend({
  /** Plain text; blank lines separate paragraphs. Never HTML. */
  body: z.string(),
});
export type ContentPage = z.infer<typeof contentPageSchema>;

const contentPageFields = {
  kind: contentPageKindSchema,
  slug: contentPageSlugSchema,
  title: requiredText(120),
  body: z.string().max(50_000),
  isPublished: z.boolean(),
  position: z.number().int().min(0).max(1000),
};

export const createContentPageRequestSchema = z.object(contentPageFields);
export type CreateContentPageRequest = z.infer<typeof createContentPageRequestSchema>;

export const updateContentPageRequestSchema = z.object({
  ...contentPageFields,
  version: versionSchema,
});
export type UpdateContentPageRequest = z.infer<typeof updateContentPageRequestSchema>;

// ---------------------------------------------------------------------------------------------
// Shipping zones (spec §04 "משלוחים")
// ---------------------------------------------------------------------------------------------

export const MAX_SHIPPING_ZONES = 50;

export const shippingZoneSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  /** The areas or cities the zone covers, as the shopper reads them. */
  description: z.string().nullable(),
  priceMinor: z.number().int().min(0),
  position: z.number().int(),
  isActive: z.boolean(),
  version: z.number().int(),
});
export type ShippingZone = z.infer<typeof shippingZoneSchema>;

const shippingZoneFields = {
  name: requiredText(80),
  description: optionalText(500),
  priceMinor: amountMinorSchema,
  position: z.number().int().min(0).max(1000),
  isActive: z.boolean(),
};

export const createShippingZoneRequestSchema = z.object(shippingZoneFields);
export type CreateShippingZoneRequest = z.infer<typeof createShippingZoneRequestSchema>;

export const updateShippingZoneRequestSchema = z.object({
  ...shippingZoneFields,
  version: versionSchema,
});
export type UpdateShippingZoneRequest = z.infer<typeof updateShippingZoneRequestSchema>;

// ---------------------------------------------------------------------------------------------
// Coupons (spec §04 "קופונים ומבצעים")
// ---------------------------------------------------------------------------------------------

/** PERCENT: `value` percent off the merchandise; FIXED: `value` agorot off it (never below 0). */
export const COUPON_KINDS = ['PERCENT', 'FIXED'] as const;
export const couponKindSchema = z.enum(COUPON_KINDS);
export type CouponKind = z.infer<typeof couponKindSchema>;

/** Codes are stored upper-case: shoppers may type them in any case. */
export const COUPON_CODE_PATTERN = /^[A-Z0-9][A-Z0-9_-]{2,31}$/;
export const couponCodeSchema = z
  .string()
  .trim()
  .toUpperCase()
  .regex(COUPON_CODE_PATTERN, '3 to 32 letters, digits, - or _');

/** A store keeps at most this many coupons (switched-off ones included). */
export const MAX_COUPONS = 500;

export const couponSchema = z.object({
  id: z.uuid(),
  code: z.string(),
  kind: couponKindSchema,
  value: z.number().int(),
  minimumOrderMinor: z.number().int().nullable(),
  startsAt: z.iso.datetime().nullable(),
  endsAt: z.iso.datetime().nullable(),
  /** Total uses allowed; null: unlimited. */
  usageLimit: z.number().int().nullable(),
  redeemedCount: z.number().int(),
  isActive: z.boolean(),
  version: z.number().int(),
  createdAt: z.iso.datetime(),
});
export type Coupon = z.infer<typeof couponSchema>;

const couponFields = {
  kind: couponKindSchema,
  value: z.number().int().min(1).max(1_000_000_000),
  minimumOrderMinor: amountMinorSchema.nullable(),
  startsAt: instantSchema.nullable(),
  endsAt: instantSchema.nullable(),
  usageLimit: z.number().int().min(1).max(1_000_000).nullable(),
  isActive: z.boolean(),
};

function percentInRange(value: { kind: CouponKind; value: number }): boolean {
  return value.kind !== 'PERCENT' || value.value <= 100;
}
const PERCENT_MESSAGE = { message: 'a percentage is 1 to 100', path: ['value'] };

/** The code cannot change after the coupon exists (orders refer to it by its code). */
export const createCouponRequestSchema = z
  .object({ code: couponCodeSchema, ...couponFields })
  .refine(percentInRange, PERCENT_MESSAGE)
  .refine(windowIsOrdered, WINDOW_MESSAGE);
export type CreateCouponRequest = z.infer<typeof createCouponRequestSchema>;

export const updateCouponRequestSchema = z
  .object({ ...couponFields, version: versionSchema })
  .refine(percentInRange, PERCENT_MESSAGE)
  .refine(windowIsOrdered, WINDOW_MESSAGE);
export type UpdateCouponRequest = z.infer<typeof updateCouponRequestSchema>;

// ---------------------------------------------------------------------------------------------
// Quantity deals (spec §04 "מבצעי כמות"): `quantity` units of the listed products for `priceMinor`
// ---------------------------------------------------------------------------------------------

/** A store keeps at most this many deals, each with at most this many products. */
export const MAX_PROMOTIONS = 100;
export const MAX_PROMOTION_PRODUCTS = 500;

export const promotionSchema = z.object({
  id: z.uuid(),
  name: z.string(),
  quantity: z.number().int(),
  priceMinor: z.number().int(),
  products: z.array(
    z.object({
      id: z.uuid(),
      name: z.string(),
      sku: z.string(),
      priceMinor: z.number().int().nullable(),
    }),
  ),
  startsAt: z.iso.datetime().nullable(),
  endsAt: z.iso.datetime().nullable(),
  isActive: z.boolean(),
  version: z.number().int(),
  createdAt: z.iso.datetime(),
});
export type Promotion = z.infer<typeof promotionSchema>;

const promotionFields = {
  /** Shown to shoppers in the cart ("3 ב-100"). */
  name: requiredText(80),
  quantity: z.number().int().min(2).max(99),
  priceMinor: amountMinorSchema.refine((value) => value > 0, 'the deal price must be above 0'),
  productIds: z
    .array(z.uuid())
    .min(1)
    .max(MAX_PROMOTION_PRODUCTS)
    .refine((ids) => new Set(ids).size === ids.length, 'duplicate product'),
  startsAt: instantSchema.nullable(),
  endsAt: instantSchema.nullable(),
  isActive: z.boolean(),
};

export const createPromotionRequestSchema = z
  .object(promotionFields)
  .refine(windowIsOrdered, WINDOW_MESSAGE);
export type CreatePromotionRequest = z.infer<typeof createPromotionRequestSchema>;

export const updatePromotionRequestSchema = z
  .object({ ...promotionFields, version: versionSchema })
  .refine(windowIsOrdered, WINDOW_MESSAGE);
export type UpdatePromotionRequest = z.infer<typeof updatePromotionRequestSchema>;

// ---------------------------------------------------------------------------------------------
// Popups (spec §04 "חלונות קופצים")
// ---------------------------------------------------------------------------------------------

export const MAX_POPUPS = 100;

export const popupSchema = z.object({
  id: z.uuid(),
  title: z.string(),
  body: z.string(),
  image: tenantMediaSchema.nullable(),
  buttonLabel: z.string().nullable(),
  link: z.string().nullable(),
  placement: popupPlacementSchema,
  startsAt: z.iso.datetime().nullable(),
  endsAt: z.iso.datetime().nullable(),
  isActive: z.boolean(),
  version: z.number().int(),
  createdAt: z.iso.datetime(),
});
export type Popup = z.infer<typeof popupSchema>;

const popupFields = {
  title: requiredText(120),
  body: z.string().trim().max(1000),
  mediaId: z.uuid().nullable(),
  buttonLabel: optionalText(40),
  link: storeLinkSchema.nullable(),
  placement: popupPlacementSchema,
  startsAt: instantSchema.nullable(),
  endsAt: instantSchema.nullable(),
  isActive: z.boolean(),
};

export const createPopupRequestSchema = z
  .object(popupFields)
  .refine(windowIsOrdered, WINDOW_MESSAGE);
export type CreatePopupRequest = z.infer<typeof createPopupRequestSchema>;

export const updatePopupRequestSchema = z
  .object({ ...popupFields, version: versionSchema })
  .refine(windowIsOrdered, WINDOW_MESSAGE);
export type UpdatePopupRequest = z.infer<typeof updatePopupRequestSchema>;

// ---------------------------------------------------------------------------------------------
// Quote requests (spec §04 "הזמנות"; adr/0022: stores take requests for a quote, not orders)
// ---------------------------------------------------------------------------------------------

/**
 * Which status a quote request may move to. Working statuses move freely (an owner may correct a
 * mistake); CANCELLED is final.
 */
export const ORDER_STATUS_TRANSITIONS: Readonly<Record<OrderStatus, readonly OrderStatus[]>> = {
  NEW: ['IN_PROGRESS', 'QUOTED', 'CLOSED', 'CANCELLED'],
  IN_PROGRESS: ['NEW', 'QUOTED', 'CLOSED', 'CANCELLED'],
  QUOTED: ['IN_PROGRESS', 'CLOSED', 'CANCELLED'],
  CLOSED: ['IN_PROGRESS', 'QUOTED'],
  CANCELLED: [],
};

export function canMoveOrder(from: OrderStatus, to: OrderStatus): boolean {
  return ORDER_STATUS_TRANSITIONS[from].includes(to);
}

const dateOnlySchema = z.iso.date();

export const orderListQuerySchema = pageQuerySchema.extend({
  status: orderStatusSchema.optional(),
  /** A request number, or part of the shopper's name, email or phone. */
  q: z.string().trim().min(1).max(100).optional(),
  /** Sent on or after this day / up to and including this day, in the store's time zone. */
  from: dateOnlySchema.optional(),
  to: dateOnlySchema.optional(),
  customerId: z.uuid().optional(),
});
export type OrderListQuery = z.infer<typeof orderListQuerySchema>;

export const orderAdminSummarySchema = z.object({
  id: z.uuid(),
  number: z.number().int(),
  status: orderStatusSchema,
  /** Only on requests sent while the form still asked how to receive the goods; null since. */
  fulfillment: fulfillmentMethodSchema.nullable(),
  contactName: z.string(),
  contactEmail: z.string(),
  contactPhone: z.string(),
  customerId: z.uuid().nullable(),
  /**
   * What the requested lines come to at the store's list prices when the request was sent (VAT
   * included). For the store only, as a starting point for its quote: shoppers never see it.
   */
  totalMinor: z.number().int(),
  itemCount: z.number().int(),
  createdAt: z.iso.datetime(),
});
export type OrderAdminSummary = z.infer<typeof orderAdminSummarySchema>;

export const orderHistoryEntrySchema = z.object({
  status: orderStatusSchema,
  changedAt: z.iso.datetime(),
  /** The staff member who changed it; null when the shopper sent the request. */
  changedBy: z.string().nullable(),
});

/** A requested line with the store's list price when the request was sent (for the store only). */
export const orderAdminItemSchema = orderItemSchema.extend({
  unitPriceMinor: z.number().int(),
  lineTotalMinor: z.number().int(),
});
export type OrderAdminItem = z.infer<typeof orderAdminItemSchema>;

export const orderAdminDetailSchema = orderAdminSummarySchema.extend({
  contact: orderContactSchema,
  address: orderAddressSchema.nullable(),
  notes: z.string().nullable(),
  /** The VAT included in `totalMinor`. */
  vatMinor: z.number().int(),
  vatRatePercent: z.number(),
  currency: currencySchema,
  items: z.array(orderAdminItemSchema),
  history: z.array(orderHistoryEntrySchema),
  /** The statuses this request may move to now. */
  nextStatuses: z.array(orderStatusSchema),
  version: z.number().int(),
  updatedAt: z.iso.datetime(),
});
export type OrderAdminDetail = z.infer<typeof orderAdminDetailSchema>;

export const updateOrderRequestSchema = z.object({
  version: versionSchema,
  status: orderStatusSchema,
});
export type UpdateOrderRequest = z.infer<typeof updateOrderRequestSchema>;

// ---------------------------------------------------------------------------------------------
// The store's quote for a request (docs/TENANT_ADMIN.md "The quote")
// ---------------------------------------------------------------------------------------------

/** Lines one quote may have. */
export const MAX_QUOTE_LINES = 200;
/** Units of one quote line. */
export const MAX_QUOTE_QUANTITY = 10_000;

/** A free text of the quote: trimmed, null when empty. */
const quoteTextSchema = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .nullable()
    .transform((value) => (value === null || value === '' ? null : value));

/** A quote line: a product of the store, or a line the store writes itself (no product). */
export const orderQuoteLineSchema = z.object({
  productId: z.uuid().nullable(),
  title: z.string(),
  sku: z.string().nullable(),
  quantity: z.number().int(),
  /** VAT included, like the list prices. */
  unitPriceMinor: z.number().int(),
  lineTotalMinor: z.number().int(),
});
export type OrderQuoteLine = z.infer<typeof orderQuoteLineSchema>;

export const orderQuoteSchema = z.object({
  /** False while the store has not saved one: the lines are then the request at list prices. */
  saved: z.boolean(),
  lines: z.array(orderQuoteLineSchema),
  validUntil: dateOnlySchema.nullable(),
  paymentTerms: z.string().nullable(),
  notes: z.string().nullable(),
  /** The lines together, VAT included, and the VAT in it. */
  totalMinor: z.number().int(),
  vatMinor: z.number().int(),
  vatRatePercent: z.number(),
  /** 0 until the quote is saved; send it back with the next save. */
  version: z.number().int(),
  updatedAt: z.iso.datetime().nullable(),
  /** The staff member who saved it last (null when unsaved, or no longer a member). */
  updatedBy: z.string().nullable(),
});
export type OrderQuote = z.infer<typeof orderQuoteSchema>;

const quoteLineQuantitySchema = z.number().int().min(1).max(MAX_QUOTE_QUANTITY);

/**
 * A line as the store saves it. A product's name and SKU are read from the store's product when
 * it is saved, never taken from the request; a line of the store's own has its own text.
 */
export const saveOrderQuoteLineSchema = z.union([
  z.object({
    productId: z.uuid(),
    quantity: quoteLineQuantitySchema,
    unitPriceMinor: amountMinorSchema,
  }),
  z.object({
    productId: z.null(),
    title: z.string().trim().min(1).max(300),
    sku: quoteTextSchema(50),
    quantity: quoteLineQuantitySchema,
    unitPriceMinor: amountMinorSchema,
  }),
]);
export type SaveOrderQuoteLine = z.infer<typeof saveOrderQuoteLineSchema>;

export const saveOrderQuoteRequestSchema = z.object({
  /** The `version` the quote was read at: 0 for the first save. */
  version: z.number().int().min(0),
  lines: z.array(saveOrderQuoteLineSchema).min(1).max(MAX_QUOTE_LINES),
  validUntil: dateOnlySchema.nullable(),
  paymentTerms: quoteTextSchema(500),
  notes: quoteTextSchema(2000),
});
export type SaveOrderQuoteRequest = z.infer<typeof saveOrderQuoteRequestSchema>;

// ---------------------------------------------------------------------------------------------
// Customers (spec §04 "לקוחות")
// ---------------------------------------------------------------------------------------------

export const customerListQuerySchema = pageQuerySchema.extend({
  /** Part of the name, email or phone. */
  q: z.string().trim().min(1).max(100).optional(),
  status: customerStatusSchema.optional(),
});
export type CustomerListQuery = z.infer<typeof customerListQuerySchema>;

export const customerAdminSummarySchema = z.object({
  id: z.uuid(),
  email: z.string(),
  fullName: z.string(),
  phone: z.string().nullable(),
  status: customerStatusSchema,
  /** Quote requests that were not cancelled. */
  orderCount: z.number().int(),
  lastOrderAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
});
export type CustomerAdminSummary = z.infer<typeof customerAdminSummarySchema>;

export const customerAdminDetailSchema = customerAdminSummarySchema.extend({
  lastLoginAt: z.iso.datetime().nullable(),
  version: z.number().int(),
  /** The customer's orders, newest first (at most 100). */
  orders: z.array(orderAdminSummarySchema),
});
export type CustomerAdminDetail = z.infer<typeof customerAdminDetailSchema>;

/** Disabling an account signs the shopper out everywhere; their orders stay. */
export const updateCustomerRequestSchema = z.object({
  version: versionSchema,
  status: customerStatusSchema,
});
export type UpdateCustomerRequest = z.infer<typeof updateCustomerRequestSchema>;

// ---------------------------------------------------------------------------------------------
// Staff (TARGET_ARCHITECTURE §12; Q13: OWNER / ADMIN / STAFF)
// ---------------------------------------------------------------------------------------------

export const inviteMemberRequestSchema = z.object({
  email: emailSchema,
  displayName: requiredText(120),
  role: tenantRoleSchema,
});
export type InviteMemberRequest = z.infer<typeof inviteMemberRequestSchema>;

export const inviteMemberResponseSchema = z.object({
  membership: membershipSchema,
  /** The link token when the person has no password yet; shown once (no email until Phase 11). */
  invitation: issuedInvitationSchema.nullable(),
});
export type InviteMemberResponse = z.infer<typeof inviteMemberResponseSchema>;

// ---------------------------------------------------------------------------------------------
// Dashboard
// ---------------------------------------------------------------------------------------------

export const storeDashboardSchema = z.object({
  store: z.object({
    name: z.string(),
    /** The storefront has opened to the public (provisioning finished). */
    opened: z.boolean(),
    /** The owner's own open/closed switch. */
    siteOpen: z.boolean(),
    /** The store's primary address, e.g. `https://shop.example.co.il`; null when none is active. */
    url: z.string().nullable(),
  }),
  orders: z.object({
    new: z.number().int(),
    inProgress: z.number().int(),
    /** Sent today in the store's time zone (cancelled excluded). */
    today: z.number().int(),
  }),
  products: z.object({
    active: z.number().int(),
    draft: z.number().int(),
    hidden: z.number().int(),
    lowStock: z.number().int(),
    outOfStock: z.number().int(),
    /** Products still waiting for the store's own copy (they cannot be shown). */
    withoutCopy: z.number().int(),
    sourceDiscontinued: z.number().int(),
    /**
     * The products not shown in the store (DRAFT or HIDDEN), by what they lack. A product that
     * lacks several things counts under each; `ready` lacks nothing.
     */
    notShown: z.object({
      noCopy: z.number().int(),
      ready: z.number().int(),
    }),
  }),
  customers: z.object({ total: z.number().int() }),
});
export type StoreDashboard = z.infer<typeof storeDashboardSchema>;
